import { useState, useCallback, useRef } from 'react';
import { AutoComplete, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { mapboxForwardGeocode, MapboxFeature } from '../../thunks/mapbox_geocoding_thunks';
import { ServiceArea } from '../../types/serviceArea';

interface MapboxSearchInputProps {
	onSelect: (area: ServiceArea) => void;
	geocodeTypes?: string;
	placeholder?: string;
	// Optional controlled text; when provided, the parent owns clearing it (on actual commit).
	value?: string;
	onValueChange?: (value: string) => void;
}

const featureToServiceArea = (feature: MapboxFeature): ServiceArea => {
	return {
		name: feature.place_name || '',
		placeType: feature.place_type && feature.place_type[0] ? feature.place_type[0] : 'region',
		center: feature.center || [0, 0],
		bbox: feature.bbox || null,
	};
};

const MapboxSearchInput = ({
	onSelect,
	geocodeTypes,
	placeholder,
	value,
	onValueChange,
}: MapboxSearchInputProps) => {
	const [options, setOptions] = useState<
		{ value: string; label: string; feature: MapboxFeature }[]
	>([]);
	const [internalValue, setInternalValue] = useState('');
	const isControlled = value !== undefined;
	const searchValue = isControlled ? (value as string) : internalValue;
	const debounceRef = useRef<any>(null);
	const featuresRef = useRef<MapboxFeature[]>([]);

	const updateSearchValue = useCallback(
		(next: string) => {
			if (!isControlled) {
				setInternalValue(next);
			}
			if (onValueChange) {
				onValueChange(next);
			}
		},
		[isControlled, onValueChange]
	);

	const handleSearch = useCallback(
		(next: string) => {
			updateSearchValue(next);
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
			if (!next || next.length < 2) {
				setOptions([]);
				featuresRef.current = [];
				return;
			}
			debounceRef.current = setTimeout(() => {
				mapboxForwardGeocode(next, geocodeTypes).then((features) => {
					featuresRef.current = features;
					setOptions(
						features.map((f, idx) => ({
							value: String(idx),
							label: f.place_name || '',
							feature: f,
						}))
					);
				});
			}, 300);
		},
		[geocodeTypes, updateSearchValue]
	);

	const handleSelect = useCallback(
		(selectedOptionValue: string) => {
			const idx = parseInt(selectedOptionValue, 10);
			const feature = featuresRef.current[idx];
			if (feature) {
				onSelect(featureToServiceArea(feature));
			}
			// Preserve typed text; parent clears via `value` / `onValueChange` on real commit.
			setOptions([]);
			featuresRef.current = [];
		},
		[onSelect]
	);

	return (
		<AutoComplete
			className="flex-1"
			options={options}
			onSearch={handleSearch}
			onSelect={handleSelect}
			value={searchValue}
		>
			<Input
				placeholder={placeholder || 'Search for a location...'}
				prefix={<SearchOutlined />}
				allowClear
			/>
		</AutoComplete>
	);
};

export default MapboxSearchInput;
